import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import {
  DomEvent,
  LatLng,
  LeafletMouseEvent,
  LineUtil,
  Map as LeafletMap,
} from 'leaflet';
import { useDispatch } from 'react-redux';
import { insertWaypointAfter } from '../store/slices/missionPlannerSlice';

/** The point on a leg the pointer is currently over, or dragging. */
export interface LegGrab {
  /** Index of the leg — also the position the new waypoint lands after. */
  index: number;
  lat: number;
  lng: number;
  /** True once the press has travelled far enough to count as a drag. */
  dragging: boolean;
}

/** Travel, in pixels, that separates a drag from a click on the line. */
const DRAG_THRESHOLD_PX = 3;

/** Foot of the perpendicular from `at` onto the leg a→b, in screen space. */
const snapToLeg = (
  map: LeafletMap,
  at: LatLng,
  a: [number, number],
  b: [number, number]
): LatLng =>
  map.layerPointToLatLng(
    LineUtil.closestPointOnSegment(
      map.latLngToLayerPoint(at),
      map.latLngToLayerPoint(a),
      map.latLngToLayerPoint(b)
    )
  );

/**
 * The handlers a route leg binds to become grabbable. Stable for the lifetime
 * of the gesture wiring, so a leg that binds them can be memoised against the
 * grab point moving.
 */
export interface LegPointerHandlers {
  /** Pointer moved over leg `index` — rest the grab point on the line. */
  trackPointer: (
    index: number,
    event: LeafletMouseEvent,
    a: [number, number],
    b: [number, number]
  ) => void;
  /** Pointer left leg `index` — drop the grab point unless a drag holds it. */
  releasePointer: (index: number) => void;
  /** Pointer pressed on leg `index` — start the insert gesture. */
  beginDrag: (index: number, event: LeafletMouseEvent) => void;
}

export interface LegDragInsert {
  /** The grab point to draw, or null when the pointer is off every leg. */
  grab: LegGrab | null;
  handlers: LegPointerHandlers;
}

/**
 * Grab any point on a route leg and drag it to insert a waypoint there (#404) —
 * the Google Maps interaction, as opposed to only being able to move waypoints
 * that already exist.
 *
 * The gesture is driven off map-level listeners rather than a draggable marker
 * on purpose. A marker under the pointer would have to appear on hover and then
 * hand the pointer over to itself, and the point being dragged has to move
 * freely off the line — neither survives a marker whose icon or position is
 * rewritten mid-gesture (the lesson of #433).
 *
 * Nothing is dispatched until the pointer is released: inserting earlier would
 * resegment the route underneath the gesture that is still running.
 */
export const useLegDragInsert = (enabled: boolean): LegDragInsert => {
  const map = useMap();
  const dispatch = useDispatch();
  const [grab, setGrab] = useState<LegGrab | null>(null);
  // Read synchronously by handlers that cannot wait for a re-render.
  const draggingRef = useRef(false);
  // Set while a drag is live so an unmount mid-gesture still unwinds it.
  const endDragRef = useRef<(() => void) | null>(null);

  useEffect(() => () => endDragRef.current?.(), []);

  const trackPointer = useCallback<LegPointerHandlers['trackPointer']>(
    (index, event, a, b) => {
      if (!enabled || draggingRef.current) {
        return;
      }
      const point = snapToLeg(map, event.latlng, a, b);
      setGrab({ index, lat: point.lat, lng: point.lng, dragging: false });
    },
    [enabled, map]
  );

  const releasePointer = useCallback<LegPointerHandlers['releasePointer']>(
    (index) => {
      if (draggingRef.current) {
        return;
      }
      setGrab((current) =>
        current && current.index === index ? null : current
      );
    },
    []
  );

  const beginDrag = useCallback<LegPointerHandlers['beginDrag']>(
    (index, event) => {
      if (!enabled || draggingRef.current || event.originalEvent.button !== 0) {
        return;
      }
      // Stop the press panning the map or reaching the map click handler that
      // would otherwise append a waypoint under the pointer.
      DomEvent.stop(event.originalEvent);
      // Restore what panning *was*, rather than assuming it was on — a map
      // configured without dragging must not gain it by being edited.
      const panningWasEnabled = map.dragging?.enabled() ?? false;
      map.dragging?.disable();
      draggingRef.current = true;

      const origin = map.latLngToLayerPoint(event.latlng);
      let moved = false;
      let at = event.latlng;

      const onMove = (moveEvent: LeafletMouseEvent): void => {
        moved =
          moved ||
          map.latLngToLayerPoint(moveEvent.latlng).distanceTo(origin) >
            DRAG_THRESHOLD_PX;
        if (!moved) {
          return;
        }
        at = moveEvent.latlng;
        setGrab({ index, lat: at.lat, lng: at.lng, dragging: true });
      };

      const end = (commit: boolean): void => {
        endDragRef.current = null;
        map.off('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.removeEventListener('touchend', onUp);
        if (panningWasEnabled) {
          map.dragging?.enable();
        }
        draggingRef.current = false;
        setGrab(null);
        // A press that never travelled is a click on the line, not an edit.
        if (commit && moved) {
          dispatch(insertWaypointAfter({ index, lat: at.lat, lng: at.lng }));
        }
      };

      function onUp(): void {
        end(true);
      }

      endDragRef.current = () => end(false);
      map.on('mousemove', onMove);
      // On the document, not the map: releasing outside the map must still end
      // the gesture, or panning would stay disabled with no way back. `touchend`
      // for the same reason — a finger lifted where the compatibility mouse
      // event never lands must not strand the map either.
      document.addEventListener('mouseup', onUp);
      document.addEventListener('touchend', onUp);
    },
    [dispatch, enabled, map]
  );

  const handlers = useMemo(
    () => ({ trackPointer, releasePointer, beginDrag }),
    [beginDrag, releasePointer, trackPointer]
  );

  return { grab, handlers };
};
